import { Component, DOCUMENT, ErrorHandler, inject, OnInit, Renderer2 } from '@angular/core';
import { NgClass, registerLocaleData } from '@angular/common';
import { ConfigurationService } from './core/configuration/configuration.service';
import * as moment from 'moment-timezone';
import 'moment/locale/de';
import { GlobalErrorHandler } from './core/error-handler/error-handler';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from './core/error-dialog/error-dialog.component';
import localeDe from '@angular/common/locales/de';
import { NGXLogger } from 'ngx-logger';
import { ScreenService } from './core/screen/screen.service';
import { Platform } from '@angular/cdk/platform';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { difference, filter as filterArray, isEmpty, split, union, uniq } from 'lodash';
import { getAppPageConfiguration, PageConfiguration } from './shared/component-base/app-page.component';
import { MatIconRegistryInitService } from './shared/mat-icon-registry-init/mat-icon-registry-init.service';
import { IconSet } from './shared/mat-icon-registry-init/icon-set';

@Component({
  selector: 'app-root',
  template: `
    <div
      [ngClass]="{
        'app-platform-ios': platform.IOS,
        'app-mobile': screen.device === 'mobile',
        'app-desktop': screen.device === 'desktop'
      }"
    >
      <router-outlet></router-outlet>
    </div>
  `,
  imports: [
    NgClass,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit {
  readonly platform = inject(Platform);
  readonly screen = inject(ScreenService);

  private readonly matIconRegistryInitService = inject(MatIconRegistryInitService);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor() {
    const configurationService = inject(ConfigurationService);
    const logger = inject(NGXLogger);
    const errorHandler = inject(ErrorHandler) as GlobalErrorHandler;
    const dialog = inject(MatDialog);

    logger.updateConfig({
      level: configurationService.logLevel,
      serverLogLevel: configurationService.serverLogLevel,
      serverLoggingUrl: configurationService.serverLoggingUrl,
    });
    logger.debug('App Komponenente wird initialisert');
    // Set timezone and locale to fixed value for whole app
    moment.tz.setDefault(configurationService.timezone);
    moment.locale(configurationService.locale);

    registerLocaleData(localeDe);

    errorHandler.getErrors().subscribe((error) => {
      dialog.open(ErrorDialogComponent, {
        data: { error },
        width: '328px',
      });
    });
  }

  private readonly DEFAULT_BODY_CLASSES = [
    'mat-app-background',
    'mat-typography',
  ];

  ngOnInit() {
    this.matIconRegistryInitService.init(IconSet);
    this.router.events
      .pipe(filter((event) => {
        return event instanceof NavigationEnd || (event instanceof Scroll && event.routerEvent instanceof NavigationEnd);
      }))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
      )
      .subscribe((route) => {
        if (route && route.component) {
          const pageConfig = getAppPageConfiguration(route.component) || {};
          this.updateBodyClasses(pageConfig);
        }
      });
  }

  private updateBodyClasses(pageConfiguration?: PageConfiguration) {
    const body = this.document?.body;
    if (!body) {
      return;
    }

    try {
      const actualBodyClasses = AppComponent.getClasses(body);
      const additionalClasses = filterArray(
        split(pageConfiguration?.bodyClasses),
        (cls) => !isEmpty(cls),
      );
      const expectedClasses = union(this.DEFAULT_BODY_CLASSES, additionalClasses);
      const classesToAdd = difference(expectedClasses, actualBodyClasses);
      const classesToRemove = difference(actualBodyClasses, expectedClasses);
      classesToRemove.forEach((cls) => {
        this.renderer.removeClass(body, cls);
      });
      classesToAdd.forEach((cls) => {
        this.renderer.addClass(body, cls);
      });
    } catch (e) {
      console.error('Unable to update body classes', e);
    }
  }

  static getClasses(element: Element): string[] {
    const result: any[] = [];
    element.classList.forEach((cls) => result.push(cls));
    return uniq(result);
  }
}
